import { createContext, useContext, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'zevi-guest-cart-v1';

const CartContext = createContext(null);

function readCart() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(readCart);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = (product, variant = null, quantity = 1) => {
    const colorName = variant?.name || '';
    const key = `${product.id || product.slug}:${variant?.id || 'default'}:${colorName}`;

    setItems((current) => {
      const existing = current.find((item) => item.key === key);
      if (existing) {
        return current.map((item) =>
          item.key === key
            ? {
                ...item,
                quantity: Math.min(
                  item.quantity + quantity,
                  product.availableStock || 99
                )
              }
            : item
        );
      }
      return [
        ...current,
        {
          key,
          productId: product.id,
          slug: product.slug,
          name: product.name,
          image: product.image,
          imageAlt: product.imageAlt || `${product.name} saree`,
          price: Number(variant?.price_override ?? product.price),
          variantId: variant?.id || null,
          variantName: variant?.name || '',
          colorName: colorName,
          quantity,
          availableStock: product.availableStock || 99
        }
      ];
    });
    setDrawerOpen(true);
  };

  const updateQuantity = (key, quantity) => {
    setItems((current) =>
      current.map((item) =>
        item.key === key
          ? {
              ...item,
              quantity: Math.max(
                1,
                Math.min(Number(quantity), item.availableStock || 99)
              )
            }
          : item
      )
    );
  };

  const removeItem = (key) => {
    setItems((current) => current.filter((item) => item.key !== key));
  };

  const clearCart = () => setItems([]);

  const itemCount = items.reduce(
    (sum, item) => sum + item.quantity,
    0
  );

  const subtotal = items.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );

  const value = useMemo(
    () => ({
      items,
      itemCount,
      subtotal,
      drawerOpen,
      setDrawerOpen,
      addItem,
      updateQuantity,
      removeItem,
      clearCart
    }),
    [items, itemCount, subtotal, drawerOpen]
  );

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const value = useContext(CartContext);
  if (!value) {
    throw new Error('useCart must be used inside CartProvider');
  }
  return value;
}